import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import axios from 'axios';
import { ScrollReveal, API } from './shared';

const ChallengeModal = ({ isOpen, onClose, challengeType, challengeTitle }) => {
  const [formData, setFormData] = useState({ name: '', email: '', organization: '', challenge_description: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [matchedCases, setMatchedCases] = useState([]);
  const [error, setError] = useState(null);

  const matchTypeLabels = { best_fit: 'Most Relevant', analogous: 'Related Evidence', credibility: 'Additional Proof Point' };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/submit-challenge`, { ...formData, challenge_type: challengeType });
      if (response.data.status === 'success') {
        setSubmitted(true);
        if (response.data.matched_cases) setMatchedCases(response.data.matched_cases);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', email: '', organization: '', challenge_description: '' });
    setSubmitted(false);
    setMatchedCases([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] bg-white border-none rounded-none p-0 overflow-hidden max-h-[85vh] overflow-y-auto">
        <div className="p-8">
          <DialogHeader className="mb-6">
            <DialogTitle className="font-serif text-2xl sm:text-3xl text-[#0B1A2B] tracking-tight">
              Share Your Challenge
            </DialogTitle>
            <DialogDescription className="text-[#334155] mt-2">
              Tell us about your business challenge. Our leadership team will review your submission and reach out to discuss how APIG can help.
            </DialogDescription>
          </DialogHeader>

          {submitted ? (
            <div className="py-4">
              <div className="text-center mb-6">
                <div className="w-12 h-12 border-2 border-[#D97706] flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#D97706] text-xl">&#10003;</span>
                </div>
                <h3 className="text-2xl font-serif text-[#0B1A2B] mb-2">Challenge Received</h3>
                <p className="text-[#334155] text-sm max-w-md mx-auto">
                  Our leadership team will review your submission and reach out within 48 hours. We've also sent relevant case evidence to your email.
                </p>
              </div>

              {matchedCases.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                  <p className="text-[10px] text-[#D97706] uppercase tracking-wider font-medium mb-4 text-center">Relevant Case Evidence</p>
                  <div className="space-y-3">
                    {matchedCases.map((mc, i) => (
                      <div key={i} className="p-4 bg-[#F8FAFC] border-l-[3px] border-[#D97706]">
                        <span className="text-[9px] text-[#D97706] uppercase tracking-wider font-medium">{matchTypeLabels[mc.match_type] || 'Relevant'}</span>
                        <h4 className="font-serif text-base text-[#0B1A2B] mt-1 mb-1">{mc.title}</h4>
                        <p className="text-xs text-[#334155] mb-1">{mc.reason}</p>
                        <p className="text-[11px] text-[#64748B] italic">{mc.quantified_outcomes}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 text-center">
                <Button onClick={handleClose} className="btn-primary rounded-none">Close</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div>}
              <div className="grid sm:grid-cols-2 gap-5">
                <input type="text" placeholder="Your Name *" className="input-editorial" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="challenge-input-name" />
                <input type="email" placeholder="Email Address *" className="input-editorial" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required data-testid="challenge-input-email" />
              </div>
              <input type="text" placeholder="Organization" className="input-editorial" value={formData.organization} onChange={(e) => setFormData({...formData, organization: e.target.value})} data-testid="challenge-input-organization" />
              <div>
                <textarea placeholder="Describe your challenge... *" className="input-editorial min-h-[150px] resize-none" value={formData.challenge_description} onChange={(e) => setFormData({...formData, challenge_description: e.target.value})} required data-testid="challenge-input-description" />
                <div className="mt-3 p-3 bg-[#F8FAFC] border border-[#E2E8F0]">
                  <p className="text-[11px] font-medium text-[#0B1A2B] mb-1.5">Not sure where to start? Consider framing around:</p>
                  <ul className="text-[11px] text-[#64748B] space-y-0.5 list-none">
                    <li>What decision or bet are you trying to de-risk?</li>
                    <li>Where is the gap — insight, positioning, or execution?</li>
                    <li>What does success look like in 6-12 months?</li>
                  </ul>
                </div>
              </div>
              <Button type="submit" disabled={isSubmitting} className="btn-primary rounded-none w-full" data-testid="challenge-submit-button">
                {isSubmitting ? 'Submitting...' : 'Submit Your Challenge'}
              </Button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ChallengesSection = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);

  const challenges = [
    {
      id: 'evidence-gap',
      title: 'We don\'t have enough evidence to place the right bet.',
      description: 'When the data says go but the market says wait, you need intelligence that captures what\'s shifting before it hits the P&L.',
    },
    {
      id: 'positioning-gap',
      title: 'We know the market is shifting, but our positioning is not keeping up.',
      description: 'Competitors are repositioning faster. Your differentiation story needs to be built on what customers commit to, not what the team assumes.',
    },
    {
      id: 'execution-gap',
      title: 'We have a strategy, but it is not converting into growth.',
      description: 'The plan was approved. Capital was deployed. But execution is fragmented and results are not materializing.',
    },
    {
      id: 'investment-gap',
      title: 'We need to know which investments will move revenue, retention, or adoption fastest.',
      description: 'In a leading dental technology engagement, this analysis found that the top 5 initiatives could affect ~70% of target customers, with ~32% willing to pay 10%+ more for a better experience.',
    },
  ];

  return (
    <section id="challenges" className="section-spacing bg-white" data-testid="challenges-section">
      <div className="container-luxury">
        <ScrollReveal>
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="overline mb-4">The Growth Gap</p>
            <h2 className="heading-section text-4xl sm:text-5xl text-[#0B1A2B] mb-6" data-testid="challenges-headline">
              The Challenges We Solve
            </h2>
            <p className="body-large">
              The quiet part of a high-stakes decision: nobody in the room knows, and everyone can feel it. APIG closes the gap between what leadership knows and what the market demands.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal>
          <div className="w-full mb-16 md:mb-20" data-testid="challenges-brand-image">
            <div className="w-full h-[320px] sm:h-[420px] md:h-[520px] lg:h-[560px] overflow-hidden">
              <img
                src={`${API}/resources/apig-team-collaboration.webp`}
                alt="APIG leadership-style executive team collaborating around a table"
                className="w-full h-full object-cover"
                style={{ objectPosition: '62% center' }}
                loading="lazy"
              />
            </div>
          </div>
        </ScrollReveal>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {challenges.map((challenge, index) => (
            <ScrollReveal key={index} delay={index * 100}>
              <div 
                className="p-8 lg:p-10 bg-white border border-[#E2E8F0] h-full flex flex-col transition-all duration-500 hover:shadow-lg hover:border-[#D97706]/30"
                data-testid={`challenge-card-${index + 1}`}
              >
                <h3 className="font-serif text-lg lg:text-xl text-[#0B1A2B] mb-4 leading-tight italic">
                  "{challenge.title}"
                </h3>
                <p className="text-[#334155] text-[0.9375rem] leading-relaxed mb-6 flex-grow">
                  {challenge.description}
                </p>
                <button 
                  onClick={() => { setSelectedChallenge(challenge); setModalOpen(true); }}
                  className="inline-flex items-center text-sm font-medium text-[#0B1A2B] hover:text-[#D97706] transition-colors duration-300"
                  data-testid={`challenge-btn-${index + 1}`}
                >
                  Share Your Challenge
                  <span className="ml-2 transition-transform duration-300 hover:translate-x-1">→</span>
                </button>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>

      {selectedChallenge && (
        <ChallengeModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          challengeType={selectedChallenge.id}
          challengeTitle={selectedChallenge.title}
        />
      )}
    </section>
  );
};

export default ChallengesSection;
